import React, { useState, useEffect } from 'react';
import { transferBranch as transferBranchSql, getPersonnelByUid } from '../../services/migration/personnelDataService';
import {
  Box,
  Typography,
  Paper,
  Modal,
  IconButton,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Radio,
  Alert,
  CircularProgress,
  styled,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  Business as BusinessIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

// Modal için styled component
const StyledModal = styled(Modal)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const ModalContent = styled(Paper)(({ theme }) => ({
  position: 'relative',
  width: '90%',
  maxWidth: 500,
  maxHeight: '80vh',
  overflow: 'auto',
  padding: theme.spacing(3),
  borderRadius: 12,
  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
}));

const ModalHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

interface PersonnelBranchTransferModalProps {
  open: boolean;
  onClose: () => void;
  personnelId: string;
  personnelName: string;
  currentBranchId: string | null;
  companyId: string;
  onBranchTransferred?: () => void;
}

const PersonnelBranchTransferModal: React.FC<PersonnelBranchTransferModalProps> = ({
  open,
  onClose,
  personnelId,
  personnelName,
  currentBranchId,
  companyId,
  onBranchTransferred
}) => {
  const { t } = useTranslation('humanResources');
  // AuthContext'ten branches ve getBranchName al
  const { branches, getBranchName } = useAuth();
  
  const [selectedBranch, setSelectedBranch] = useState<string | null>(currentBranchId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [currentBranchName, setCurrentBranchName] = useState<string>('');
  const [confirmRemoveDialogOpen, setConfirmRemoveDialogOpen] = useState(false);

  // ✅ Cloud Functions ile modal açılınca şube durumunu yükle (TEK SEFERLİK)
  useEffect(() => {
    const loadPersonnelBranch = async () => {
      if (open && personnelId && companyId) {
        console.log(`[PersonnelBranchTransferModal] Personel şube bilgisi yükleniyor: ${personnelId}`);
        
        try {
          const personnelData = await getPersonnelByUid(personnelId, companyId);
          
          if (personnelData) {
            const personnelBranchId = (personnelData.branchesId as string | null) || null;
            
            console.log(`[PersonnelBranchTransferModal] Personel şube bilgisi yüklendi:`, personnelBranchId);
            
            setSelectedBranch(personnelBranchId);
            setCurrentBranchName(getBranchName(personnelBranchId));
          }
        } catch (error) {
          console.error('[PersonnelBranchTransferModal] Şube bilgisi yüklenirken hata:', error);
        }
      }
    };
    
    loadPersonnelBranch();
  }, [open, personnelId, companyId, getBranchName]);

  // branches artık AuthContext'ten geliyor, ayrı yükleme gerekmiyor
  // Modal açıldığında mevcut şube bilgisini ayarla
  useEffect(() => {
    if (open && currentBranchId) {
      setSelectedBranch(currentBranchId);
      setCurrentBranchName(getBranchName(currentBranchId));
    } else if (open) {
      setSelectedBranch(null);
      setCurrentBranchName('');
    }
  }, [open, currentBranchId, getBranchName]);

  // Şube seçme işlemi
  const handleSelectBranch = (branchId: string) => {
    setSelectedBranch(branchId);
  };

  // Şube aktarma işlemi
  const handleTransferToBranch = async () => {
    if (!selectedBranch || !personnelId || !companyId) return;
    
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Faz 3: sube transferi SQL'e (personnelDataService.transferBranch). RTDB'ye yazilmaz.
      await transferBranchSql(personnelId, selectedBranch);
      
      const selectedBranchObj = branches.find(branch => branch.id === selectedBranch);
      const branchName = selectedBranchObj ? selectedBranchObj.name : t('remaining.personnelBranchTransfer.selectedBranchFallback');
      
      setSuccess(t('remaining.personnelBranchTransfer.messages.transferSuccess', { branchName }));
      
      setTimeout(() => {
        onClose();
      }, 2000);
      
      if (onBranchTransferred) {
        onBranchTransferred();
      }
    } catch (error) {
      console.error('Şubeye aktarma hatası:', error);
      setError(t('remaining.personnelBranchTransfer.messages.transferError'));
    } finally {
      setSaving(false);
    }
  };

  // Silme onay diyaloğunu aç
  const handleOpenRemoveDialog = () => {
    setConfirmRemoveDialogOpen(true);
  };

  // Silme onay diyaloğunu kapat
  const handleCloseRemoveDialog = () => {
    setConfirmRemoveDialogOpen(false);
  };

  // Şube atamasını kaldırma işlemi
  const handleRemoveBranchAssignment = async () => {
    if (!personnelId || !companyId) return;
    
    setSaving(true);
    setError(null);
    setSuccess(null);
    setConfirmRemoveDialogOpen(false);
    
    try {
      // Faz 3: sube atamasi kaldirma SQL'e (branchesId=null). RTDB'ye yazilmaz.
      await transferBranchSql(personnelId, null);
      
      setSelectedBranch(null);
      setSuccess(t('remaining.personnelBranchTransfer.messages.removeSuccess', { branchName: currentBranchName }));
      
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Şube ataması kaldırma hatası:', error);
      setError(t('remaining.personnelBranchTransfer.messages.removeError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <StyledModal
        open={open}
        onClose={onClose}
        aria-labelledby="şube-aktarma-modal"
      >
        <ModalContent>
          <ModalHeader>
            <Typography variant="h6" fontWeight="bold">
              {t('remaining.personnelBranchTransfer.title')}
            </Typography>
            <IconButton onClick={onClose} disabled={saving}>
              <CloseIcon />
            </IconButton>
          </ModalHeader>
          
          <Typography variant="subtitle1" gutterBottom>
            {t('remaining.personnelBranchTransfer.description', { personnelName })}
          </Typography>
          
          {currentBranchId && (
            <Alert 
              severity="info" 
              sx={{ mb: 2 }}
            >
              {t('remaining.personnelBranchTransfer.currentBranchInfo', { branchName: currentBranchName })}
            </Alert>
          )}
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}
          
          {branches.length === 0 ? (
            <Alert severity="info" sx={{ my: 2 }}>
              {t('remaining.personnelBranchTransfer.noBranches')}
            </Alert>
          ) : (
            <List
              sx={{
                maxHeight: 300,
                overflow: 'auto',
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                mt: 2
              }}
            >
              {branches.map((branch) => (
                <React.Fragment key={branch.id}>
                  <ListItem disablePadding>
                    <ListItemButton 
                      onClick={() => handleSelectBranch(branch.id)}
                      selected={selectedBranch === branch.id}
                    >
                      <ListItemIcon>
                        <Radio 
                          checked={selectedBranch === branch.id} 
                          onChange={() => handleSelectBranch(branch.id)}
                        />
                      </ListItemIcon>
                      <ListItemText 
                        primary={branch.name} 
                        secondary={
                          <>
                            <Typography variant="body2" component="span">{branch.address}</Typography>
                            {branch.id === currentBranchId && (
                              <Typography 
                                variant="body2" 
                                component="span" 
                                sx={{ 
                                  color: 'success.main',
                                  fontWeight: 'bold',
                                  display: 'block'
                                }}
                              >
                                ({t('remaining.personnelBranchTransfer.currentBranchTag')})
                              </Typography>
                            )}
                          </>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                  <Divider component="li" />
                </React.Fragment>
              ))}
            </List>
          )}
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={onClose}
              disabled={saving}
            >
              {currentBranchId ? t('remaining.personnelBranchTransfer.actions.cancel') : t('remaining.personnelPage.k023')}
            </Button>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              {currentBranchId && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleOpenRemoveDialog}
                  disabled={saving}
                  startIcon={<DeleteIcon />}
                >
                  {t('remaining.personnelBranchTransfer.actions.removeFromBranch')}
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={handleTransferToBranch}
                disabled={!selectedBranch || saving || (selectedBranch === currentBranchId)}
                startIcon={<BusinessIcon />}
              >
                {saving ? <CircularProgress size={24} /> : t('remaining.personnelBranchTransfer.actions.transfer')}
              </Button>
            </Box>
          </Box>
        </ModalContent>
      </StyledModal>
      
      {/* Şube atamasını kaldırma onay diyaloğu */}
      <Dialog
        open={confirmRemoveDialogOpen}
        onClose={handleCloseRemoveDialog}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          {t('remaining.personnelDetailModal.branchOps.removeAssignment')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('remaining.personnelDetailModal.branchOps.removeAssignmentConfirm', { personnelName, branchName: currentBranchName })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRemoveDialog} color="inherit">
            {t('remaining.personnelPage.k023')}
          </Button>
          <Button onClick={handleRemoveBranchAssignment} color="error" variant="contained" autoFocus>
            {t('remaining.personnelBranchTransfer.actions.removeFromBranch')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PersonnelBranchTransferModal; 